import { t } from '../../strings';

const AUTH_KEY = 'syrinx.pb.auth';

export interface PbAuth {
  token: string;
  userId: string;
  email: string;
}

export class AuthExpiredError extends Error {
  constructor() {
    super(t('app.errors.sessionExpired'));
  }
}

/**
 * Owns the PocketBase base URL, the persisted auth token, and the fetch wrapper
 * every API call goes through. An expired session drops the token and fires
 * `onExpired`, which main.ts wires back to the sign-in screen.
 *
 * `baseUrl` is '' in the deployed setup: PocketBase serves the app and the API
 * from one origin, so requests are same-origin and there is no CORS to config.
 */
export class PbSession {
  auth: PbAuth | null;
  onExpired: () => void = () => {};

  constructor(readonly baseUrl: string) {
    this.auth = readStoredAuth();
  }

  get userId(): string {
    if (!this.auth) throw new AuthExpiredError();
    return this.auth.userId;
  }

  get email(): string {
    return this.auth?.email ?? '';
  }

  private setAuth(auth: PbAuth | null): void {
    this.auth = auth;
    if (auth) localStorage.setItem(AUTH_KEY, JSON.stringify(auth));
    else localStorage.removeItem(AUTH_KEY);
  }

  signOut(): void {
    this.setAuth(null);
  }

  /**
   * Is a PocketBase answering at the configured origin? A 200 alone isn't
   * enough — a static server (or a dev server's SPA fallback) happily returns
   * index.html for /api/health, which would make "wrong password" the reported
   * cause of a misconfigured origin. Require the JSON body PocketBase sends.
   */
  async healthCheck(): Promise<boolean> {
    try {
      const res = await fetch(`${this.baseUrl}/api/health`, {
        signal: AbortSignal.timeout(5000),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as { code?: unknown };
      return typeof body?.code === 'number';
    } catch {
      return false; // network failure, or a non-JSON body
    }
  }

  async login(email: string, password: string): Promise<void> {
    const res = await fetch(`${this.baseUrl}/api/collections/users/auth-with-password`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ identity: email, password }),
    });
    if (!res.ok) {
      throw new Error(
        res.status === 400
          ? t('app.errors.wrongPassword')
          : t('app.errors.signInFailed', { status: res.status }),
      );
    }
    const data = (await res.json()) as {
      token: string;
      record: { id: string; email: string };
    };
    this.setAuth({ token: data.token, userId: data.record.id, email: data.record.email });
  }

  /** Validate the persisted token against the server; false → sign-in needed. */
  async tryRefresh(): Promise<boolean> {
    if (!this.auth) return false;
    try {
      const res = await fetch(`${this.baseUrl}/api/collections/users/auth-refresh`, {
        method: 'POST',
        headers: { Authorization: this.auth.token },
      });
      if (!res.ok) {
        this.setAuth(null);
        return false;
      }
      const data = (await res.json()) as {
        token: string;
        record: { id: string; email: string };
      };
      this.setAuth({
        token: data.token,
        userId: data.record.id,
        email: data.record.email,
      });
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Authenticated fetch; non-2xx throws. PocketBase treats an invalidated token
   * as a guest rather than answering 401, so a dead session surfaces as
   * 400/403/404 from the owner-scoped rules — on those, probe auth-refresh to
   * tell an expired session apart from a genuine bad request.
   */
  async request(path: string, init: RequestInit = {}): Promise<Response> {
    if (!this.auth) {
      this.onExpired();
      throw new AuthExpiredError();
    }
    const res = await fetch(`${this.baseUrl}${path}`, {
      ...init,
      headers: {
        'Content-Type': 'application/json',
        Authorization: this.auth.token,
        ...(init.headers ?? {}),
      },
    });
    if (res.ok) return res;
    const body = await res.text();
    const maybeExpired = [400, 401, 403, 404].includes(res.status);
    if (maybeExpired && !(await this.probeAuthValid())) {
      this.setAuth(null);
      this.onExpired();
      throw new AuthExpiredError();
    }
    throw new Error(`PocketBase ${init.method ?? 'GET'} ${path} failed: ${res.status} ${body}`);
  }

  /** Is the current token still good? Server hiccups don't count as expiry. */
  private async probeAuthValid(): Promise<boolean> {
    if (!this.auth) return false;
    try {
      const res = await fetch(`${this.baseUrl}/api/collections/users/auth-refresh`, {
        method: 'POST',
        headers: { Authorization: this.auth.token },
      });
      return res.ok || res.status >= 500;
    } catch {
      return true; // network failure ≠ invalid session
    }
  }
}

function readStoredAuth(): PbAuth | null {
  try {
    const raw = localStorage.getItem(AUTH_KEY);
    if (!raw) return null;
    const rec = JSON.parse(raw) as Partial<PbAuth>;
    if (typeof rec.token !== 'string' || typeof rec.userId !== 'string') return null;
    return {
      token: rec.token,
      userId: rec.userId,
      email: typeof rec.email === 'string' ? rec.email : '',
    };
  } catch {
    return null;
  }
}
